import { Component, Input, ViewContainerRef } from "@angular/core";
import * as composeActions from "@bca-compose/store/compose.actions";
import { DialogService } from "@bca-core/modules/dialog/services/dialog.service";
import { PageScrollService } from "@bca-core/modules/page-scroll/services/page-scroll.service";
import { EventBusClickService } from "@bca-core/services/event-bus.service";
import { RouterService } from "@bca-core/services/router.service";
import { UIService } from "@bca-core/services/ui.service";
import { ReceiptDialog } from "@bca-receipt/receipt/receipt-dialog";
import * as receiptActions from "@bca-receipt/store/receipt.actions";
import {
  DialogIdentifier,
  Route,
  ScrollPositionId,
} from "@bca-shared/models/_enums";
import { DialogId } from "@bca-shared/models/_types";
import { pageData } from "@bca-shared/shared.constants";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import {
  selectIncompletedProducts,
  selectTotalPrice,
  selectTotalProductCount,
} from "@bca-store/store.selectors";
import * as userDataActions from "@bca-user-data/store/user-data.actions";
import { environment } from "@environments/environment";
import { select, Store } from "@ngrx/store";
import { filter, map, take } from "rxjs/operators";
import { TabComponent } from "./tab-stepper/tab/tab.component";

@Component({
  selector: "bca-flow-window",
  templateUrl: "bca-flow-window.component.html",
})
export class BcaFlowWindowComponent {
  @Input()
  showMobileReceiptFooter$ = this._uiService.showMobileReceiptFooter$;
  currentRoute$ = this._store$.pipe(select(({ app }) => app.currentRoute));
  totalProductCount$ = this._store$.pipe(select(selectTotalProductCount));
  totalReceiptPrice$ = this._store$.pipe(select(selectTotalPrice));
  isReceiptDialogOpen$ = this._store$.pipe(
    select(({ app }) => app.dialogStatus[DialogIdentifier.Receipt])
  );
  stickyReceipt$ = this.totalProductCount$.pipe(map((count) => count < 4));
  showPhoneNumber$ = this._store$.pipe(select(({ app }) => app.showPhone));
  highestRoute$ = this._store$.select(({ app }) => app.highestRoute);
  productsLoading$ = this._store$.pipe(
    select(({ compose }) => compose.productsLoading)
  );
  selectedProductCompose$ = this._store$.pipe(
    select(({ compose }) => compose.selectedProduct)
  );
  incompletedProducts$ = this._store$.pipe(select(selectIncompletedProducts));
  redirectToIssuerLoading$ = this._store$.pipe(
    select(({ confirmation }) => confirmation.redirectToIssuerLoading)
  );

  showProductInfo$ = this._store$.pipe(
    select(selectTotalProductCount),
    map((count) => count === 1)
  );

  Route = Route;
  maxBuyableProducts = environment.receipt.maxBuyableProducts;
  currentRoute: Route;
  isThereAIncompleteProduct: boolean;

  pageData = pageData;

  constructor(
    private _routerService: RouterService,
    private _store$: Store<IRootState>,
    private _uiService: UIService,
    private _dialogService: DialogService,
    private _eventBus: EventBusClickService,
    private _page: PageScrollService,
    private _viewContainerRef: ViewContainerRef
  ) {}

  nextRoute(): void {
    this.currentRoute$.subscribe((route) => (this.currentRoute = route));

    if (this.currentRoute === Route.COMPOSE) {
      this.selectedProductCompose$
        .pipe(take(1))
        .subscribe((selectedProduct) => {
          this._store$.dispatch(
            this._createAction(
              new userDataActions.SelectProduct(selectedProduct)
            )
          );

          if (selectedProduct) {
            this._store$.dispatch(
              this._createAction(new composeActions.SetConfiguredProduct(null))
            );
            this._store$.dispatch(
              this._createAction(new receiptActions.AddProduct(selectedProduct))
            );
            this._store$.dispatch(
              this._createAction(new composeActions.SelectProduct(null))
            );
          }
        });
      this._store$.dispatch(this._createAction(new appActions.NextRoute()));
    } else {
      this._uiService.nextPageClicked$.next();
    }
  }

  addNewProduct(): void {
    this.currentRoute$.subscribe((route) => (this.currentRoute = route));

    if (this.currentRoute === Route.COMPOSE) {
      this.selectedProductCompose$
        .pipe(
          take(1),
          filter((selectedProduct) => selectedProduct != null)
        )
        .subscribe((selectedProduct) => {
          this._store$.dispatch(
            this._createAction(new composeActions.SetConfiguredProduct(null))
          );
          this._store$.dispatch(
            this._createAction(new receiptActions.AddProduct(selectedProduct))
          );
          this._store$.dispatch(
            this._createAction(new composeActions.SelectProduct(null))
          );
          this._page.scrollTo(ScrollPositionId.TOP, {
            timeout: 275,
            scrollOffset: 250,
          });
        });
    } else {
      this._uiService.addProductClicked$.next();
    }
  }

  onTabClicked(tab: TabComponent): void {
    this.currentRoute$.subscribe((route) => (this.currentRoute = route));
    this.incompletedProducts$.subscribe((product) => {
      if (product.length) {
        this.isThereAIncompleteProduct = true;
      } else {
        this.isThereAIncompleteProduct = false;
      }
    });

    if (
      this.currentRoute === Route.COMPOSE &&
      this.isThereAIncompleteProduct &&
      tab.route > 1
    ) {
      return;
    }
    this._uiService.tabClicked$.next(tab);
    const { done } = tab;
    if (done) {
      this._routerService.navigateTo(tab.route);
    }
  }

  onReceiptOpened(): void {
    const heigth = window.scrollY;
    const receiptDialog = this._dialogService.open(
      ReceiptDialog,
      DialogIdentifier.Receipt,
      this._viewContainerRef,
      null,
      null,
      (visible: boolean, identifier: DialogId) => {
        this._store$.dispatch(
          this._createAction(
            new appActions.SetDialogStatus({ visible, identifier })
          )
        );
      }
    );

    this._eventBus.closeClicked().subscribe((clicked) => {
      if (clicked === true) {
        receiptDialog.afterClosed().subscribe(() => {
          setTimeout(() => {
            window.scrollTo({
              top: heigth,
              left: 0,
              behavior: "smooth",
            });
          }, 100);
        });
      }
    });
    receiptDialog.instance.nextPage$().subscribe(() => this.nextRoute());
    receiptDialog.instance.addProduct$().subscribe(() => this.addNewProduct());
  }

  onActionClicked(action: appActions.Action): void {
    this._store$.dispatch(this._createAction(action));
  }

  private _createAction(action: IAction): IAction {
    return createAction(action, BcaFlowWindowComponent);
  }
}
